import { Logger } from '@saviynt/common';

import {
  hasNewEntitlements,
  processHybridPayload,
} from './hybridProcessingUtils';
import { replacePlaceholdersWithIndex } from './placeholderIndexUtils';

interface PayloadProcessingContext {
  payload: Record<string, unknown>;
  currentIndex: number;
  allPrompts: Record<number, string>;
  currentPrompt?: string | null;
  totalEntitlements?: number;
  originalTemplate?: string;
  accessType?: 'AddAccess' | 'RemoveAccess';
  stepFormData?: Record<string, any>;
  entitlements?: { value: string }[];
}

/**
 * Create Mode: Process accumulated prompts individually (cumulative)
 * - Processes prompts up to current index
 * - Keeps placeholders for future entitlements
 */
export const processCreateModePayload = (
  context: PayloadProcessingContext
): Record<string, unknown> => {
  const { payload, currentIndex, allPrompts, currentPrompt } = context;

  // Build prompts array with accumulated prompts up to current index
  const promptsArray: (string | null)[] = [];

  for (let i = 0; i <= currentIndex; i += 1) {
    const prompt = allPrompts[i];

    promptsArray[i] = prompt && prompt.trim() !== '' ? prompt : null;
  }

  // Add current prompt if provided (for "Specify Prompt" button)
  if (currentPrompt && currentPrompt.trim() !== '') {
    promptsArray[currentIndex] = currentPrompt;
  }

  return replacePlaceholdersWithIndex(
    payload,
    currentPrompt ?? null,
    currentIndex,
    promptsArray
  );
};

/**
 * Edit Mode: Process with hybrid approach for new entitlements
 * - Existing entitlements: complete processing (edit mode)
 * - New entitlements: cumulative processing (create mode)
 */
export const processEditModePayload = (
  context: PayloadProcessingContext
): Record<string, unknown> => {
  const {
    payload,
    currentIndex,
    allPrompts,
    currentPrompt,
    totalEntitlements,
    originalTemplate,
    accessType,
    stepFormData,
    entitlements,
  } = context;

  // Check if hybrid processing is needed and all required context is available
  if (
    originalTemplate &&
    accessType &&
    stepFormData &&
    entitlements &&
    hasNewEntitlements(originalTemplate, entitlements)
  ) {
    try {
      return processHybridPayload({
        originalTemplate,
        currentEntitlements: entitlements,
        stepFormData,
        allPrompts,
        currentPrompt,
        currentIndex,
        accessType,
      });
    } catch (error) {
      Logger.warn(
        'Hybrid processing failed, falling back to standard edit mode:',
        error
      );
    }
  }

  // Standard edit mode processing for existing entitlements only
  const maxIndex = totalEntitlements ? totalEntitlements - 1 : currentIndex;
  const promptsArray: (string | null)[] = [];

  for (let i = 0; i <= maxIndex; i += 1) {
    const prompt = allPrompts[i];

    promptsArray[i] = prompt && prompt.trim() !== '' ? prompt : null;
  }

  if (currentPrompt && currentPrompt.trim() !== '') {
    promptsArray[currentIndex] = currentPrompt;
  }

  return replacePlaceholdersWithIndex(
    payload,
    currentPrompt ?? null,
    maxIndex,
    promptsArray
  );
};

/**
 * Unified payload processor that handles both create and edit modes
 */
export const processPayloadByMode = (
  isEditMode: boolean,
  context: PayloadProcessingContext
): Record<string, unknown> => {
  if (isEditMode) {
    return processEditModePayload(context);
  }

  return processCreateModePayload(context);
};

/**
 * Check if current environment is edit mode
 */
export const isEditModeFromUrl = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.has('endpointKey');
};
